// @givmo/board — which board a director page is about: `?org=<org_ref>`.
//
// An account may hold seats on several boards (contract §6), so a board page names its board in its
// address. The address is the backend's own: a claimed seat's packet notice links
// `{console}/board/director/packets/{packet_id}?org={org_ref}` (contract §12.4, Q8), pinned on both
// sides like the invitation's link. The board is checked against `/me`'s directory, and the backend
// re-runs its seam on every call regardless: a directory entry opens nothing by itself.
//
// Without `org`: one board → that board, by a replace (no Back-button trap); several or none → the
// boards page, which lists them.

import { useEffect } from 'react';
import { useNavigate as useRouterNavigate, useRouterState } from '@tanstack/react-router';
import { useBoardBasePath } from '../board-runtime';
import { boardPath, useSearch } from '../board-routing';
import { BOARD_DIRECTOR_PAGES } from '../board-routes';
import { committeeOnlyWords as co } from '../features/committees/committee-only-words';
import { directorBoardFor, useDirectorMe, useListedSeat } from './director-me';
import { isCommitteeOnly, type DirectorBoard, type DirectorMe } from './director-wire';

/** The search key every board page carries. */
export const DIRECTOR_BOARD_SEARCH_KEY = 'org';

/** `org` from a search, or null. */
export function directorOrgOf(search: Record<string, unknown>): string | null {
  const value = search[DIRECTOR_BOARD_SEARCH_KEY];
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

export interface DirectorBoardAddress {
  /** The board the address names, or null while it is unknown or absent. */
  orgRef: string | null;
  /** Its directory entry, once `/me` has answered and lists it. */
  board: DirectorBoard | null;
  me: DirectorMe | undefined;
  /** `/me` has answered (or failed): the page may decide what to show. */
  settled: boolean;
  /** The address names a board this account holds no seat on. */
  notYours: boolean;
  /**
   * Whose seat the page's header speaks of: the seat `/me` lists here, or, once it no longer does,
   * the seat it last listed here (CBG-FIX104, row 4.1's memory) — so a committee member shut out
   * of their seat reads the committee's header words around the line that says so.
   */
  scope: SeatScope;
}

/** A seat's scope, for the words a page's header chooses by it. */
export interface SeatScope {
  /** A seat for someone who isn't on the board (`isCommitteeOnly`). */
  committeeOnly: boolean;
  /** The names of its committees. */
  committees: readonly string[];
}

/** The board this page is about, resolving a missing `org` against the directory. */
export function useDirectorBoardAddress(): DirectorBoardAddress {
  const search = useSearch();
  const meQuery = useDirectorMe();
  const navigate = useRouterNavigate();
  const basePath = useBoardBasePath();
  const location = useRouterState({ select: (state) => state.location });
  const named = directorOrgOf(search);
  const me = meQuery.data;

  useEffect(() => {
    if (named !== null || me === undefined) return;
    const only = me.boards.length === 1 ? me.boards[0] : undefined;
    if (only !== undefined) {
      void navigate({
        to: location.pathname,
        search: {
          ...(location.search as Record<string, unknown>),
          [DIRECTOR_BOARD_SEARCH_KEY]: only.org_ref,
        },
        replace: true,
      });
      return;
    }
    void navigate({ to: boardPath(basePath, BOARD_DIRECTOR_PAGES.home), replace: true });
  }, [named, me, navigate, location.pathname, location.search, basePath]);

  const board = directorBoardFor(me, named);
  const listed = useListedSeat(named);
  return {
    orgRef: named,
    board,
    me,
    settled: meQuery.isSuccess || meQuery.isError,
    notYours: meQuery.isSuccess && named !== null && board === null,
    scope:
      board !== null
        ? { committeeOnly: isCommitteeOnly(board), committees: seatCommitteeNames(board) }
        : { committeeOnly: listed?.committeeOnly === true, committees: listed?.names ?? [] },
  };
}

/** A board page's search: the board it is about. */
export function directorBoardSearch(orgRef: string): Record<string, string> {
  return { [DIRECTOR_BOARD_SEARCH_KEY]: orgRef };
}

/**
 * The header's identity line for a page about one board: "Dana Whitfield · Harborlight Youth Arts
 * board" — the seat's own name as the organization recorded it, and the board. A seat for someone
 * who isn't on the board (CBG-YR6-OUTSIDE) names its committee instead ("Nadia Brennan · Harborlight
 * Youth Arts Audit Committee"), or the organization alone for a seat on several.
 */
export function directorBoardIdentity(board: DirectorBoard | null): string | undefined {
  if (board === null) return undefined;
  const organization = board.organization_name?.trim();
  const seat = board.display_name.trim();
  if (!organization) return seat.length > 0 ? seat : undefined;
  const committees = seatCommitteeNames(board);
  const where = !isCommitteeOnly(board)
    ? `${organization} board`
    : committees.length === 1
      ? `${organization} ${committees[0]}`
      : organization;
  return seat.length > 0 ? `${seat} · ${where}` : where;
}

/** The names of the committees a seat serves on, as the registry records them. */
export function seatCommitteeNames(board: DirectorBoard | null): string[] {
  return (board?.committees ?? []).map((row) => row.name).filter((name) => name.length > 0);
}

/**
 * The seat this account lands on (CBG-FIX109; Jonathan, 2026-10-17 20:48 PDT: "even though 99% of
 * our users will only be on one board?"): its one seat, when that seat opens something. Home then
 * replaces itself with that seat's page, and the page needs no way back to a list of one. A seat for
 * someone who isn't on the board opens nothing once its every committee membership has ended, so
 * such an account stays home, where its card says so. Two or more seats, or none: null, and home
 * is the list.
 */
export function landingSeat(me: DirectorMe | undefined): DirectorBoard | null {
  const boards = me?.boards ?? [];
  const only = boards.length === 1 ? boards[0] : undefined;
  if (only === undefined) return null;
  return isCommitteeOnly(only) && seatCommitteeNames(only).length === 0 ? null : only;
}

/**
 * The title of the page one seat lands on, a board's or one committee's: the officer page's own name
 * (CBG-UX10, D-CBG-40: every director page takes its officer page's name; it was "Your meetings").
 */
export const MEETINGS_TITLE = 'Meetings';

/** Where a way home goes, and what it says. */
export interface WayHome {
  to: string;
  search?: Record<string, string>;
  words: string;
  /** It opens the one seat's page, not home. */
  toOneSeat: boolean;
}

/**
 * A way home: the account menu's row and a page's way back (CBG-FIX106 row 4.7's rule: each names
 * the page it opens). Home, in the words the caller gives — or, for an account home lands on one seat
 * for (`landingSeat`), that seat's page, straight there, in its own title, "Meetings" (review-238's
 * F1: the orchestrator's call under row 4.7, approved with CBG-FIX111's copy page, its §7, in-chat
 * 2026-10-18 17:23 PDT, "approved"; the title CBG-UX10's). Home's own words name a list such an
 * account never sees. A
 * page about a board the account holds no seat on then shows that seat's page once, in its line
 * (`director-board-content.tsx`): its header leaves its own way back out rather than say it twice.
 */
export function wayHome(me: DirectorMe | undefined, homeWords: string): WayHome {
  const landing = landingSeat(me);
  return landing === null
    ? { to: BOARD_DIRECTOR_PAGES.home, words: homeWords, toOneSeat: false }
    : {
        to: BOARD_DIRECTOR_PAGES.meetings,
        search: directorBoardSearch(landing.org_ref),
        words: MEETINGS_TITLE,
        toOneSeat: true,
      };
}

/**
 * Whether every seat this account holds is a committee's, for someone who isn't on the board
 * (CBG-YR6-OUTSIDE, frame o7): home is then "Your committees", and the ways to it say so too
 * (CBG-FIX104, row 4.2). False with no seat, and before `/me` answers.
 */
export function holdsOnlyCommitteeSeats(me: DirectorMe | undefined): boolean {
  const boards = me?.boards ?? [];
  return boards.length > 0 && boards.every((board) => isCommitteeOnly(board));
}

/**
 * Home's title, which the way home and the account menu say too, so each names the page it opens
 * (CBG-FIX104 row 4.2, CBG-FIX106 row 4.7): "Your committees" while every seat the account holds is
 * a committee's, "Your boards" while it holds a board's, and the form true of both, "Your boards and
 * committees", wherever home can't know whose seats they are or were — before `/me` answers, when it
 * can't, and for an account holding no seat, never invited or left with none by a refusal
 * (review-233's F3).
 */
export function directorHomeTitle(me: { isSuccess: boolean; data?: DirectorMe }): string {
  if (!me.isSuccess || (me.data?.boards ?? []).length === 0) return 'Your boards and committees';
  return holdsOnlyCommitteeSeats(me.data) ? co.homeTitle : 'Your boards';
}
